import React, { useMemo, useContext, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CalendarBox from "../components/CalendarBox";
import MoonHover from "../components/MoonHover";
import DayHoverPreview from "../components/DayHoverPreview";
import { getMonthArrowOffset, shouldIgnoreMonthArrowKey } from "../utils/calendarKeyboard";
import DayScoreCardModal from "../components/DayScoreCardModal";
import { UserContext } from "../App";
import {
  ArrowLeftIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  CalendarIcon,
  ChartBarSquareIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { getTradingPerformance, computeWinLossStats } from '../statsUtils';
import { db } from '../firebase';
import { collection, doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import YearSelector from '../components/YearSelector';
import { getCreatedTimeMsFromCreated } from "../utils/dateUtils";
import { isTapeReading } from '../utils/tradeHelpers';
import { buildDayCapitalLookup, getDayPercentage } from '../utils/dayCapital';
import { formatCurrencyAdaptive, formatCurrencyCompact } from "../utils/numberFormat";
import AutoFitText from "../components/AutoFitText";
import WeeklyReviewStrip from "../components/WeeklyReviewStrip";
import { buildWeeklyReview } from '../utils/weeklyReviews';
import { getDefaultTradeEnvFields, resolveTradeEnvFields } from '../utils/tradeEnvFields';
import {
  ensureWeeklyReviewNotebookPage,
  refreshWeeklyReviewNotebookPage,
  saveWeeklyReviewReflections,
} from '../utils/weeklyReviewNotebook';
import {
  DEFAULT_CALENDAR_CARD_APPEARANCE,
  sanitizeCalendarCardAppearance,
} from '../utils/calendarCardAppearance';

const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DEFAULT_BE_THRESHOLD = 20;
const DEFAULT_CALENDAR_VALUE_MODE = 'pnl';

function sanitizeBeThreshold(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_BE_THRESHOLD;
  if (parsed < 1) return 1;
  if (parsed > 1000) return 1000;
  return Math.round(parsed);
}

function calculateAverageRr(trades) {
  if (!trades.length) return null;
  const values = trades
    .map((entry) => Number(entry.rr))
    .filter((value) => Number.isFinite(value));
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function getLatestFieldValue(trades, fieldName) {
  if (!trades.length) return null;
  const sorted = [...trades].sort((a, b) => getCreatedTimeMsFromCreated(b.created) - getCreatedTimeMsFromCreated(a.created));
  const match = sorted.find((entry) => entry[fieldName] !== undefined && entry[fieldName] !== null && String(entry[fieldName]).trim() !== "");
  return match ? match[fieldName] : null;
}

function formatPerformanceLabel(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
  const parsed = Number(value);
  if (parsed >= 999999) return "New";
  return `${parsed > 0 ? "+" : ""}${parsed.toFixed(1)}%`;
}

function formatAverageRrLabel(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
  return `${Number(value).toFixed(2)}R`;
}
const MonthPage = () => {
  const { currentUser, selectedAccount, dataRefreshTrigger } = useContext(UserContext);
  const { year, month } = useParams();
  const navigate = useNavigate();
  const monthIndex = parseInt(month, 10) - 1;
  const monthName = new Date(year, monthIndex).toLocaleString('default', { month: 'long' });
  const daysInMonth = new Date(year, month, 0).getDate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rulesTracking, setRulesTracking] = useState({});
  const [todayRulesResponse, setTodayRulesResponse] = useState(null); // 'yes', 'no', or null
  const [todayDateKey, setTodayDateKey] = useState('');
  const [monthPnl, setMonthPnl] = useState(0);
  const [totalTrades, setTotalTrades] = useState(0);
  const [goalPercentage, setGoalPercentage] = useState(20);
  const [beThreshold, setBeThreshold] = useState(DEFAULT_BE_THRESHOLD);
  const [calendarCardAppearance, setCalendarCardAppearance] = useState(DEFAULT_CALENDAR_CARD_APPEARANCE);
  const [calendarValueMode, setCalendarValueMode] = useState(DEFAULT_CALENDAR_VALUE_MODE);
  const [tradeEnvFields, setTradeEnvFields] = useState(getDefaultTradeEnvFields);
  const [weeklyReviewState, setWeeklyReviewState] = useState(null);
  const [weeklyReviewSaving, setWeeklyReviewSaving] = useState(false);
  const [weeklyReviewOpen, setWeeklyReviewOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedDayCard, setSelectedDayCard] = useState(null);
  const [isMonthCardOpen, setIsMonthCardOpen] = useState(false);
  const calendarGridRef = useRef(null);
  const weeklyReviewSaveTimerRef = useRef(null);
  const weeklyPnlGridClass = isFullscreen
    ? 'grid-cols-[repeat(7,minmax(0,1fr))_11rem]'
    : 'grid-cols-7 sm:grid-cols-[repeat(7,minmax(0,1fr))_9.75rem]';

  // Handle fullscreen toggle
  const toggleFullscreen = async () => {
    if (!calendarGridRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await calendarGridRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // ← / → page between months (ignored while typing or when a dialog is open).
  const hasOpenCalendarDialog = Boolean(selectedDayCard) || isMonthCardOpen || weeklyReviewOpen;
  useEffect(() => {
    const handleKeyDown = (event) => {
      const offset = getMonthArrowOffset(event.key);
      if (!offset || hasOpenCalendarDialog || shouldIgnoreMonthArrowKey(event)) return;
      event.preventDefault();
      const target = new Date(parseInt(year, 10), parseInt(month, 10) - 1 + offset, 1);
      navigate(`/month/${target.getFullYear()}/${target.getMonth() + 1}`);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [year, month, navigate, hasOpenCalendarDialog]);

  useEffect(() => {
    if (!currentUser || !selectedAccount) return;
    const fetchEntries = async () => {
      setLoading(true);
      const { db } = await import('../firebase');
      const { collection, getDocs } = await import('firebase/firestore');
      const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', selectedAccount.id, 'entries');
      const snap = await getDocs(entriesCol);
      const data = snap.docs.map(doc => doc.data());
      setEntries(data);
      setLoading(false);
    };
    fetchEntries();
  }, [currentUser, selectedAccount, year, month, dataRefreshTrigger]);

  // Get today's date string in YYYY-MM-DD format
  const getTodayDateString = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  // Keep today's date key current so header rules binding follows the current day
  useEffect(() => {
    setTodayDateKey(getTodayDateString());
    const checkInterval = setInterval(() => {
      const currentDate = getTodayDateString();
      setTodayDateKey(prev => (prev === currentDate ? prev : currentDate));
    }, 60000);
    return () => clearInterval(checkInterval);
  }, []);

  // Handle rules response (yes or no)
  const handleRulesResponse = async (response) => {
    if (!currentUser || !todayDateKey) return;
    
    try {
      const rulesDocRef = doc(db, 'users', currentUser.uid, 'rulesTracking', todayDateKey);
      await setDoc(rulesDocRef, {
        response: response, // 'yes' or 'no'
        date: todayDateKey,
        timestamp: new Date().toISOString()
      }, { merge: true });
      
      setTodayRulesResponse(response);
    } catch (error) {
      console.error('Error saving rules response:', error);
      alert('Failed to save rules response. Please try again.');
    }
  };

  // Fetch rules tracking data in real time so Journal/Month stay synced
  useEffect(() => {
    if (!currentUser) return;
    const rulesCol = collection(db, 'users', currentUser.uid, 'rulesTracking');
    const unsubscribe = onSnapshot(rulesCol, (rulesSnap) => {
      const rulesData = {};
      rulesSnap.docs.forEach((rulesDoc) => {
        rulesData[rulesDoc.id] = rulesDoc.data();
      });
      setRulesTracking(rulesData);
    }, (error) => {
      console.error('Error listening to rules tracking:', error);
    });
    return () => unsubscribe();
  }, [currentUser, dataRefreshTrigger]);

  // Keep header Yes/No state synced with rulesTracking map updates
  useEffect(() => {
    if (!currentUser || !todayDateKey) return;
    setTodayRulesResponse(rulesTracking[todayDateKey]?.response || null);
  }, [currentUser, rulesTracking, todayDateKey]);


  // Load goal percentage from settings
  useEffect(() => {
    if (!currentUser) return;
    
    const loadSettings = async () => {
      try {
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        const prefsSnap = await getDoc(prefsRef);
        
        if (prefsSnap.exists()) {
          const data = prefsSnap.data();
          if (data.goalPercentage !== undefined) {
            setGoalPercentage(data.goalPercentage);
          }
          setBeThreshold(sanitizeBeThreshold(data.beThreshold));
          setCalendarCardAppearance(sanitizeCalendarCardAppearance(data.calendarCardAppearance));
          setCalendarValueMode(data.calendarValueMode === 'rr' ? 'rr' : DEFAULT_CALENDAR_VALUE_MODE);
          setTradeEnvFields(resolveTradeEnvFields(data));
        } else {
          setTradeEnvFields(getDefaultTradeEnvFields());
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    
    loadSettings();
  }, [currentUser]);

  const computedWeeklyReview = useMemo(() => buildWeeklyReview({
    account: selectedAccount,
    entries,
    fieldDefinitions: tradeEnvFields,
    beThreshold,
  }), [selectedAccount, entries, tradeEnvFields, beThreshold]);

  useEffect(() => {
    if (!currentUser?.uid || !selectedAccount?.id || !computedWeeklyReview) {
      setWeeklyReviewState(null);
      return undefined;
    }
    let cancelled = false;
    ensureWeeklyReviewNotebookPage({
      uid: currentUser.uid,
      account: selectedAccount,
      review: computedWeeklyReview,
    }).then((result) => {
      if (!cancelled) setWeeklyReviewState(result);
    }).catch((error) => {
      console.error('Error preparing weekly review:', error);
      if (!cancelled) setWeeklyReviewState({ review: computedWeeklyReview, persisted: false, error: true });
    });
    return () => { cancelled = true; };
  }, [currentUser, selectedAccount, computedWeeklyReview]);

  useEffect(() => () => {
    if (weeklyReviewSaveTimerRef.current) clearTimeout(weeklyReviewSaveTimerRef.current);
  }, []);

  const handleWeeklyReviewReflections = (reflections) => {
    if (!weeklyReviewState?.review) return;
    const nextReview = { ...weeklyReviewState.review, reflections };
    setWeeklyReviewState((current) => ({ ...current, review: nextReview }));
    if (weeklyReviewSaveTimerRef.current) clearTimeout(weeklyReviewSaveTimerRef.current);
    setWeeklyReviewSaving(true);
    weeklyReviewSaveTimerRef.current = setTimeout(async () => {
      try {
        const result = await saveWeeklyReviewReflections({
          uid: currentUser.uid,
          account: selectedAccount,
          review: nextReview,
          reflections,
        });
        if (result.review) {
          setWeeklyReviewState((current) => ({ ...current, review: result.review }));
        }
      } catch (error) {
        console.error('Error saving weekly review reflections:', error);
      } finally {
        setWeeklyReviewSaving(false);
      }
    }, 500);
  };

  const handleWeeklyReviewRefresh = async () => {
    const pendingReview = weeklyReviewState?.pendingReview;
    if (!pendingReview) return;
    setWeeklyReviewSaving(true);
    try {
      const result = await refreshWeeklyReviewNotebookPage({
        uid: currentUser.uid,
        account: selectedAccount,
        review: pendingReview,
        reflections: weeklyReviewState.review?.reflections,
      });
      setWeeklyReviewState({ ...result, persisted: true, dataChanged: false });
    } catch (error) {
      console.error('Error refreshing weekly review:', error);
    } finally {
      setWeeklyReviewSaving(false);
    }
  };

  // Aggregate P&L by day and calculate percentages
  const dayData = useMemo(() => {
    const data = Array(daysInMonth).fill(null).map(() => ({
      pnl: 0,
      rrTotal: 0,
      rrCount: 0,
      count: 0,
      entryCount: 0,
      percentage: 0,
      progressTowardGoal: 0,
    }));
    
    const getCapitalBeforeDay = buildDayCapitalLookup(entries, selectedAccount);

    // Aggregate P&L by day; entryCount = trades + tape readings (any non-deposit/payout/reset)
    entries.forEach(entry => {
      if (String(entry.year) === String(year) && String(entry.month) === String(month)) {
        const idx = parseInt(entry.day, 10) - 1;
        if (idx >= 0 && idx < daysInMonth) {
          const isActualTrade = !entry.isDeposit && !entry.isPayout && !isTapeReading(entry) && !entry.isResetExcluded && entry.pnl !== undefined && entry.pnl !== null && entry.pnl !== "";
          if (isActualTrade) {
            data[idx].pnl += Number(entry.pnl) || 0;
            data[idx].count += 1;
            if (entry.rr !== undefined && entry.rr !== null && String(entry.rr).trim() !== "") {
              const rrValue = Number(entry.rr);
              if (Number.isFinite(rrValue)) {
                data[idx].rrTotal += rrValue;
                data[idx].rrCount += 1;
              }
            }
          }
          if (!entry.isDeposit && !entry.isPayout && !entry.isResetExcluded) {
            data[idx].entryCount += 1;
          }
        }
      }
    });
    
    // Calculate percentage and goal progress for each day
    data.forEach((day, dayIdx) => {
      if (day.count === 0) return;
      const capital = getCapitalBeforeDay(year, month, dayIdx + 1);
      day.percentage = getDayPercentage(day.pnl, capital);
      day.progressTowardGoal = capital > 0
        ? Math.min(100, Math.max(0, (day.percentage / goalPercentage) * 100))
        : 0;
    });
    
    return data;
  }, [entries, year, month, daysInMonth, goalPercentage, selectedAccount]);

  // Calculate total month PNL and total trades
  useEffect(() => {
    if (!entries.length) {
      setMonthPnl(0);
      setTotalTrades(0);
      return;
    }
    
    const monthEntries = entries.filter(e => 
      String(e.year) === String(year) && 
      String(e.month) === String(month) &&
      !e.isDeposit && 
      !e.isPayout && 
      !isTapeReading(e) &&
      !e.isResetExcluded &&
      e.pnl !== undefined &&
      e.pnl !== null &&
      e.pnl !== ""
    );
    
    const totalPnl = monthEntries.reduce((sum, e) => sum + (Number(e.pnl) || 0), 0);
    setMonthPnl(totalPnl);
    setTotalTrades(monthEntries.length);
  }, [entries, year, month]);

  const calendarGrid = useMemo(() => {
    const firstDayOfMonth = new Date(year, month - 1, 1);
    const firstDayWeekday = firstDayOfMonth.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    const grid = [];
    
    // Calculate offset for all 7 days of the week
    // Sunday (0) -> offset 0, Monday (1) -> offset 1, ..., Saturday (6) -> offset 6
    const weekdayOffset = firstDayWeekday;
    
    let week = Array(weekdayOffset).fill(null);
    
    // Iterate through all days of the month, including weekends
    for (let d = 1; d <= daysInMonth; d++) {
      // Add day to current week
      week.push(d);
      
      // When week reaches 7 days, start a new week
      if (week.length === 7) {
        grid.push(week);
        week = [];
      }
    }
    
    // Add remaining days if any
    if (week.length) grid.push(week);
    
    return grid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, daysInMonth, dataRefreshTrigger]);

  // Helper to compute weekly PnL and trade counts for the month
  function getWeeklyPnls(entries, year, month, calendarGrid) {
    // Only entries in the given month that are actual trades
    const filtered = entries.filter(e => 
      String(e.year) === String(year) && 
      String(e.month) === String(month) &&
      !e.isDeposit && 
      !e.isPayout && 
      !isTapeReading(e) &&
      !e.isResetExcluded &&
      e.pnl !== undefined &&
      e.pnl !== null &&
      e.pnl !== ""
    );
    // Build a map day->PnL and day->count
    const dayPnls = {};
    const dayCounts = {};
    filtered.forEach(e => {
      const day = parseInt(e.day, 10);
      if (!isNaN(day)) {
        dayPnls[day] = (dayPnls[day] || 0) + (Number(e.pnl) || 0);
        dayCounts[day] = (dayCounts[day] || 0) + 1;
      }
    });
    // For each week in the grid, sum the PnL and count for the days in that week
    return calendarGrid.map(weekArr => {
      let sum = 0;
      let count = 0;
      weekArr.forEach(day => {
        if (day && Object.prototype.hasOwnProperty.call(dayPnls, day)) {
          sum += dayPnls[day];
          count += dayCounts[day] || 0;
        }
      });
      return { pnl: sum, count };
    });
  }

  const weeklyPnls = useMemo(() => getWeeklyPnls(entries, year, month, calendarGrid), [entries, year, month, calendarGrid]);

  // Hover preview rows per day: actual trades only (same filter as the day P&L).
  const dayPreviews = useMemo(() => {
    const byDay = {};
    entries.forEach((entry) => {
      if (String(entry.year) !== String(year) || String(entry.month) !== String(month)) return;
      if (entry.isDeposit || entry.isPayout || isTapeReading(entry) || entry.isResetExcluded) return;
      if (entry.pnl === undefined || entry.pnl === null || entry.pnl === "") return;
      const day = parseInt(entry.day, 10);
      if (!Number.isFinite(day)) return;
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(entry);
    });
    const previews = {};
    Object.keys(byDay).forEach((dayKey) => {
      const day = Number(dayKey);
      const dayEntries = [...byDay[dayKey]].sort((a, b) => (
        getCreatedTimeMsFromCreated(a.created) - getCreatedTimeMsFromCreated(b.created)
      ));
      previews[day] = {
        dateLabel: new Date(Number(year), Number(month) - 1, day).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        }),
        trades: dayEntries.map((entry, index) => ({
          key: entry.id || `${entry.created || ''}-${index}`,
          label: String(entry.title || '').trim() || String(entry.tickerTraded || entry.ticker || '').trim() || 'Trade',
          pnl: Number(entry.pnl) || 0,
        })),
        total: dayEntries.reduce((sum, entry) => sum + (Number(entry.pnl) || 0), 0),
      };
    });
    return previews;
  }, [entries, year, month]);

  const monthEntriesForCard = useMemo(() => (
    entries.filter((entry) =>
      String(entry.year) === String(year) &&
      String(entry.month) === String(month) &&
      !entry.isDeposit &&
      !entry.isPayout &&
      !entry.isResetExcluded
    )
  ), [entries, year, month]);

  const monthTradesForCard = useMemo(() => (
    monthEntriesForCard.filter((entry) =>
      !isTapeReading(entry) &&
      entry.pnl !== undefined &&
      entry.pnl !== null &&
      entry.pnl !== ""
    )
  ), [monthEntriesForCard]);

  const monthPerformance = useMemo(
    () => getTradingPerformance(entries, year, month),
    [entries, year, month]
  );

  const monthAverageRr = useMemo(
    () => calculateAverageRr(monthTradesForCard),
    [monthTradesForCard]
  );

  const monthWinLossStats = useMemo(
    () => computeWinLossStats(monthTradesForCard, beThreshold),
    [monthTradesForCard, beThreshold]
  );

  const bestDayPnl = useMemo(() => {
    if (!monthTradesForCard.length) return null;
    const pnlByDay = {};
    monthTradesForCard.forEach((entry) => {
      const dayKey = String(entry.day);
      pnlByDay[dayKey] = (pnlByDay[dayKey] || 0) + (Number(entry.pnl) || 0);
    });
    const dayValues = Object.values(pnlByDay);
    return dayValues.length ? Math.max(...dayValues) : null;
  }, [monthTradesForCard]);

  const monthCardStats = useMemo(() => ([
    { label: "Performance", value: formatPerformanceLabel(monthPerformance?.percentage) },
    { label: "Avg R:R", value: formatAverageRrLabel(monthAverageRr) },
    { label: "Win Rate", value: monthTradesForCard.length ? `${monthWinLossStats.winRate.toFixed(0)}%` : "-" },
    { label: "Best Day", value: bestDayPnl === null ? "-" : formatCurrencyCompact(bestDayPnl) },
  ]), [bestDayPnl, monthAverageRr, monthPerformance?.percentage, monthTradesForCard.length, monthWinLossStats.winRate]);

  const hasMonthCardData = monthEntriesForCard.length > 0;
  const hasMonthPerformance = Boolean(monthPerformance?.hasTrades);
  const monthPerformancePercentage = Number(monthPerformance?.percentage);
  const safeMonthPerformancePercentage = Number.isFinite(monthPerformancePercentage) ? monthPerformancePercentage : 0;
  const progressTowardGoal = hasMonthPerformance && goalPercentage > 0
    ? Math.min(100, Math.max(0, (safeMonthPerformancePercentage / goalPercentage) * 100))
    : 0;
  const progressBarTone = progressTowardGoal >= 100
    ? 'theme-progress-fill-success'
    : 'theme-progress-fill-accent';
  const rulesButtonBaseClass = 'inline-flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full theme-choice-button';
  const yesRulesButtonClass = `${rulesButtonBaseClass} ${todayRulesResponse === 'yes' ? 'theme-choice-button-success-active' : 'theme-choice-button-success'}`;
  const noRulesButtonClass = `${rulesButtonBaseClass} ${todayRulesResponse === 'no' ? 'theme-choice-button-danger-active' : 'theme-choice-button-danger'}`;

  const openDayCard = ({ day, pnl, count, percentage }) => {
    const dayString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayTrades = entries.filter((entry) =>
      String(entry.year) === String(year) &&
      String(entry.month) === String(month) &&
      String(entry.day) === String(day) &&
      !entry.isDeposit &&
      !entry.isPayout &&
      !isTapeReading(entry) &&
      !entry.isResetExcluded
    );

    setSelectedDayCard({
      dateKey: dayString,
      dateLabel: new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      pnl,
      rr: calculateAverageRr(dayTrades),
      percentage,
      session: getLatestFieldValue(dayTrades, "tradeSession"),
      grade: getLatestFieldValue(dayTrades, "tradeRating"),
    });
  };

  // Year dropdown: dynamic range based on current year (same as HomePage and CalendarPage)
  const currentYear = new Date().getFullYear();
  const goToMonthOffset = (offset) => {
    const target = new Date(parseInt(year, 10), parseInt(month, 10) - 1 + offset, 1);
    navigate(`/month/${target.getFullYear()}/${target.getMonth() + 1}`);
  };
  const todayParts = todayDateKey ? todayDateKey.split('-').map(Number) : null;
  const isViewingTodayMonth = !todayParts
    || (todayParts[0] === parseInt(year, 10) && todayParts[1] === parseInt(month, 10));
  const goToTodayMonth = () => {
    const now = new Date();
    navigate(`/month/${now.getFullYear()}/${now.getMonth() + 1}`);
  };
  const monthNavButtonClass = "inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full border border-[#E5E7EB] dark:border-[#374151] bg-white/90 dark:bg-[#0F172A]/80 text-[#111827] dark:text-[#F9FAFB] shadow-subtle transition-colors hover:bg-[#F3F4F6] dark:hover:bg-[#1F2937] shrink-0";
  const todayButtonClass = "h-8 sm:h-9 items-center justify-center rounded-full border border-[#E5E7EB] dark:border-[#374151] bg-white/90 dark:bg-[#0F172A]/80 px-3 text-xs sm:text-sm font-medium text-[#111827] dark:text-[#F9FAFB] shadow-subtle transition-colors hover:bg-[#F3F4F6] dark:hover:bg-[#1F2937] shrink-0";
  const yearOptions = [];
  for (let y = currentYear - 3; y <= currentYear + 2; y++) yearOptions.push(y);

  const weeklyReviewAnchorDay = (() => {
    const review = weeklyReviewState?.review;
    if (!review?.startKey || !review?.endKey) return null;
    const reviewStart = new Date(`${review.startKey}T00:00:00`);
    const reviewEnd = new Date(`${review.endKey}T23:59:59`);
    const visibleStart = new Date(Number(year), Number(month) - 1, 1);
    const visibleEnd = new Date(Number(year), Number(month), 0, 23, 59, 59);
    if (reviewEnd < visibleStart || reviewStart > visibleEnd) return null;
    return new Date(Math.max(reviewStart.getTime(), visibleStart.getTime())).getDate();
  })();

  return (
    <div className="page-shell pt-4 sm:pt-6 animate-fadeIn">
      {loading ? (
        <div className="flex flex-col gap-6 w-full max-w-5xl mx-auto py-24">
          <div className="bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card h-16 w-1/3 animate-pulse mb-4 shadow-subtle" />
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
            {[...Array(56)].map((_,i) => <div key={i} className="bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card h-24 animate-pulse shadow-subtle" />)}
          </div>
        </div>
      ) : (
        <>
          {/* Header Bar */}
          <div className="bg-white dark:bg-[#111827] border-b border-[#E5E7EB] dark:border-[#6B7280] -mx-4 sm:-mx-6 lg:-mx-8 px-3 sm:px-6 lg:px-8 py-2.5 sm:py-3 mb-3 sm:mb-5">
            <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 lg:gap-4 2xl:flex-nowrap">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 lg:flex-nowrap">
                <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="inline-flex h-9 sm:h-10 items-center gap-2 rounded-full border border-[#E5E7EB] dark:border-[#374151] bg-white/90 dark:bg-[#0F172A]/80 px-2.5 sm:px-3 text-xs sm:text-sm font-medium text-[#111827] dark:text-[#F9FAFB] shadow-subtle transition-colors hover:bg-[#F3F4F6] dark:hover:bg-[#1F2937]"
                    aria-label="Back"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">Back</span>
                  </button>
                  <div className="hidden lg:block h-7 w-px bg-[#E5E7EB] dark:bg-[#374151]" />
                </div>
                <div className="flex min-w-0 items-center gap-2 sm:gap-2.5">
                  <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#3B82F6] shrink-0" />
                  <button
                    type="button"
                    onClick={() => goToMonthOffset(-1)}
                    className={monthNavButtonClass}
                    aria-label="Previous month"
                    title="Previous month"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <span className="text-lg sm:text-[1.7rem] leading-none font-semibold text-[#111827] dark:text-[#F9FAFB] whitespace-nowrap">{monthName}</span>
                  <button
                    type="button"
                    onClick={() => goToMonthOffset(1)}
                    className={monthNavButtonClass}
                    aria-label="Next month"
                    title="Next month"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                  <YearSelector
                    value={parseInt(year, 10)}
                    onChange={(newYear) => navigate(`/month/${newYear}/${month}`)}
                    min={2000}
                    max={2100}
                    size="compact"
                    className="ml-0.5 shrink-0"
                  />
                  {!isViewingTodayMonth && (
                    <button
                      type="button"
                      onClick={goToTodayMonth}
                      className={`hidden sm:inline-flex ${todayButtonClass}`}
                      title="Go to current month"
                    >
                      Today
                    </button>
                  )}
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3">
                <div className="theme-pill inline-flex h-10 sm:h-11 items-center gap-2 rounded-full px-3 sm:px-4 whitespace-nowrap">
                    <span className="theme-text text-[11px] sm:text-sm font-medium whitespace-nowrap">Goal: {goalPercentage}%</span>
                    <div className="theme-progress-track w-20 sm:w-28 rounded-full h-1.5 relative overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ease-out rounded-full ${progressBarTone}`}
                        style={{ width: `${progressTowardGoal}%` }}
                      />
                    </div>
                    <span className="theme-text min-w-[30px] text-right text-[11px] sm:text-sm font-semibold">{progressTowardGoal.toFixed(0)}%</span>
                </div>
                <div className="theme-pill inline-flex h-10 sm:h-11 items-center gap-2 rounded-full px-3 sm:px-3.5 whitespace-nowrap">
                  <span className="theme-text text-[11px] sm:text-sm font-medium whitespace-nowrap">Follow rules?</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleRulesResponse('yes')}
                      className={yesRulesButtonClass}
                      title="Yes, I followed my rules"
                    >
                      <CheckIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleRulesResponse('no')}
                      className={noRulesButtonClass}
                      title="No, I did not follow my rules"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Phones: the month row is full, so Today sits at the start of the totals row. */}
              {!isViewingTodayMonth && (
                <button
                  type="button"
                  onClick={goToTodayMonth}
                  className={`inline-flex sm:hidden ${todayButtonClass}`}
                  title="Go to current month"
                >
                  Today
                </button>
              )}
              {/* Total Month PNL and Total Trades - positioned all the way right */}
              <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3 flex-nowrap">
                {hasMonthCardData && (
                  <button
                    type="button"
                    onClick={() => setIsMonthCardOpen(true)}
                    className="inline-flex h-10 sm:h-11 items-center gap-1.5 sm:gap-2 rounded-full border border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#1F2937] px-3 sm:px-4 text-xs sm:text-sm font-medium text-[#111827] dark:text-[#F9FAFB] shadow-subtle transition-colors hover:bg-[#F3F4F6] dark:hover:bg-[#374151] whitespace-nowrap"
                  >
                    <PhotoIcon className="w-3.5 h-3.5" />
                    <span>Month Card</span>
                  </button>
                )}
                {/* PNL Pill */}
                <div className={`number-display inline-flex h-10 sm:h-11 items-center px-3.5 sm:px-4 rounded-full border text-sm sm:text-[15px] font-semibold whitespace-nowrap ${
                  monthPnl > 0 ? 'theme-success-soft theme-success-border' :
                  monthPnl < 0 ? 'theme-danger-soft theme-loss-border' :
                  'bg-[#6B7280] text-black dark:text-white'
                }`}>
                  {monthPnl > 0 ? '+' : ''}{formatCurrencyAdaptive(monthPnl)}
                </div>
                {/* Total Trades Pill */}
                <div
                  className="number-display inline-flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-full text-sm sm:text-[15px] font-semibold bg-blue-300/80 dark:bg-[#1E40AF] text-black dark:text-white shadow-lg dark:shadow-[0_0_10px_rgba(96,165,250,0.6)] text-center"
                  title="Total trades"
                >
                  {totalTrades}
                </div>
              </div>
            </div>
          </div>
          <div className={`relative w-full ${isFullscreen ? 'h-full overflow-hidden bg-[#FAFAFA] px-4 pb-2 pt-3 dark:bg-[#111827] flex flex-col' : ''}`} ref={calendarGridRef}>
            {/* Fullscreen Button - positioned to align with calendar icon horizontally, weekday headers vertically */}
            <button
              onClick={toggleFullscreen}
              className="absolute left-0 p-2 hidden sm:block bg-white dark:bg-[#1F2937] hover:bg-[#F3F4F6] dark:hover:bg-[#374151] text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#111827] dark:hover:text-[#F9FAFB] border border-[#E5E7EB] dark:border-[#374151] rounded-card transition-colors"
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              style={{ top: isFullscreen ? '12px' : '0px', left: isFullscreen ? '10px' : '0px' }}
            >
              {isFullscreen ? (
                <ArrowsPointingInIcon className="w-5 h-5" />
              ) : (
                <ArrowsPointingOutIcon className="w-5 h-5" />
              )}
            </button>
            <div className={isFullscreen ? 'flex min-h-0 flex-1 flex-col' : 'overflow-x-auto'}>
              <div className={isFullscreen ? 'flex min-h-0 flex-1 flex-col' : 'min-w-0 sm:min-w-[900px]'}>
                <div className={`grid ${weeklyPnlGridClass} ${isFullscreen ? 'gap-1.5 mb-1.5' : 'gap-1.5 sm:gap-2 md:gap-3 mb-2 sm:mb-4'} ml-0 sm:ml-4 md:ml-12`}>
                  {weekdays.map(w => (
                    <div key={w} className={`text-center text-[#111827] dark:text-[#F9FAFB] font-semibold pb-1 tracking-wide ${isFullscreen ? 'text-lg' : 'text-[11px] sm:text-xs md:text-sm lg:text-base'}`}>
                      {/* Responsive labels - full word on large screens, abbreviated on small */}
                      <span className="hidden sm:block">{w}</span>
                      <span className="block sm:hidden">{w.substring(0, 3)}</span>
                    </div>
                  ))}
                  <div className={`${isFullscreen ? '' : 'hidden sm:block'} text-center text-[#111827] dark:text-[#F9FAFB] font-semibold pb-1 tracking-wide ${isFullscreen ? 'text-lg' : 'text-[11px] sm:text-xs md:text-sm lg:text-base'}`}>
                    <span className="hidden sm:block">Week PnL</span>
                    <span className="block sm:hidden">Week</span>
                  </div>
                </div>
                <div
                  className={isFullscreen ? 'grid min-h-0 flex-1 gap-1.5 pb-1' : ''}
                  style={isFullscreen ? { gridTemplateRows: `repeat(${calendarGrid.length}, minmax(0, 1fr))` } : undefined}
                >
                {calendarGrid.map((weekArr, weekIdx) => {
                  // Only render if the week has at least one real day in the month
                  const hasRealDay = weekArr.some(day => !!day);
                  if (!hasRealDay) return null;
                  return (
                    <div
                      key={weekIdx}
                      data-testid={`weekly-pnl-row-${weekIdx}`}
                      className={`grid min-h-0 ${weeklyPnlGridClass} ${isFullscreen ? 'h-full gap-1.5 items-stretch' : 'gap-1.5 sm:gap-2 md:gap-3 mb-1 sm:mb-1.5 md:mb-2 items-start'} ml-0 sm:ml-4 md:ml-12`}
                    >
                      {weekArr.map((day, idx) => {
                        if (!day) return <div key={idx} className="" />;
                        const { pnl, rrTotal, rrCount, count, entryCount, percentage } = dayData[day - 1];
                        let color = "gray", status = "no-trades";
                        if (count > 0) {
                          const absPnl = Math.abs(pnl);
                          // Check for BE trades using configurable threshold
                          if (absPnl >= 1 && absPnl <= beThreshold) {
                            color = "blue";
                            status = "trades";
                          } else if (pnl > 0) { 
                            color = "green"; 
                            status = "trades"; 
                          } else if (pnl < 0) { 
                            color = "red"; 
                            status = "trades"; 
                          } else { 
                            color = "gray"; 
                            status = "trades"; 
                          }
                        }
                        
                        // Rules checkmark should reflect the rulesTracking doc for that day.
                        const dayString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const dayRules = rulesTracking[dayString];
                        const dayTrades = entries.filter(e => 
                          String(e.year) === String(year) && 
                          String(e.month) === String(month) && 
                          String(e.day) === String(day) &&
                          !e.isDeposit && 
                          !e.isPayout && 
                          !isTapeReading(e) && 
                          !e.isResetExcluded
                        );
                        const rulesFollowed = dayRules?.response === 'yes' && dayTrades.length > 0;
                        
                        return (
                          <MoonHover key={idx}>
                          <DayHoverPreview preview={dayPreviews[day]}>
                          <CalendarBox
                            label={day}
                            color={color}
                            pnl={pnl}
                            rr={rrCount > 0 ? rrTotal : null}
                            valueMode={calendarValueMode}
                            status={status}
                            tradeCount={count}
                            percentage={percentage}
                            rulesFollowed={rulesFollowed}
                            onClick={() => navigate(`/day/${year}/${month}/${day}`)}
                            onOpenDayCard={() => openDayCard({ day, pnl, count, percentage })}
                            dayCardLabel={`Open day card for ${monthName} ${day}`}
                            delay={weekIdx * 0.02 + idx * 0.01}
                            compact={isFullscreen}
                            entryCountOnly={count === 0 ? entryCount : 0}
                            appearance={calendarCardAppearance}
                          />
                          </DayHoverPreview>
                          </MoonHover>
                        );
                      })}
                      {/* Fill out the rest of the 7 columns if weekArr is short */}
                      {Array.from({ length: 7 - weekArr.length }).map((_, i) => (
                        <div key={`empty-${i}`} className="" />
                      ))}
                      {/* Weekly PnL bubble - floating card */}
                      <div
                        data-testid={`weekly-pnl-cell-${weekIdx}`}
                        className={`${isFullscreen ? 'flex' : 'hidden sm:flex'} justify-center items-center h-full w-full self-center`}
                      >
                        {(() => {
                          const { pnl, count } = weeklyPnls[weekIdx];
                          const hasWeeklyReview = Boolean(
                            weeklyReviewState?.review
                            && weeklyReviewAnchorDay
                            && weekArr.includes(weeklyReviewAnchorDay)
                          );
                          const cardPnl = hasWeeklyReview ? weeklyReviewState.review.totalPnl : pnl;
                          const cardCount = hasWeeklyReview ? weeklyReviewState.review.tradeCount : count;
                          const displayPnl = typeof cardPnl === 'number' ? Number(cardPnl.toFixed(2)) : 0;
                          const isZeroWeek = displayPnl === 0;
                          const valueColor = displayPnl > 0
                            ? 'var(--theme-success)'
                            : displayPnl < 0
                              ? 'var(--theme-loss)'
                              : 'var(--theme-text)';
                          const CardElement = hasWeeklyReview ? 'button' : 'div';
                          return (
                            <CardElement
                              type={hasWeeklyReview ? 'button' : undefined}
                              onClick={hasWeeklyReview ? () => setWeeklyReviewOpen(true) : undefined}
                              data-testid={`weekly-pnl-card-${weekIdx}`}
                              data-week-pnl-state={isZeroWeek ? 'empty' : 'value'}
                              aria-label={hasWeeklyReview
                                ? `Open weekly review for ${weeklyReviewState.review.weekLabel}`
                                : isZeroWeek ? 'No weekly PnL' : `Week PnL ${displayPnl > 0 ? '+' : ''}${formatCurrencyAdaptive(displayPnl, { prefix: '' })}`}
                              title={hasWeeklyReview ? 'Open weekly review' : undefined}
                              className={`theme-panel theme-border w-full max-w-none overflow-hidden rounded-card border ${isZeroWeek ? 'shadow-none' : 'shadow-subtle'} ${isFullscreen ? 'h-[4.75rem]' : 'h-[4.25rem] sm:h-[4.5rem] md:h-[4.75rem]'} flex items-stretch ${hasWeeklyReview ? 'cursor-pointer text-left transition-colors hover:brightness-110' : ''}`}
                            >
                              {(!isZeroWeek || hasWeeklyReview) ? (
                                <>
                                  <div className={`flex min-w-0 flex-col items-center justify-center overflow-hidden px-1.5 py-1 ${hasWeeklyReview ? 'w-[78%]' : 'w-full'}`} style={{ color: valueColor }}>
                                    <div className="flex w-full items-center justify-center overflow-hidden">
                                      <AutoFitText
                                        minPx={isFullscreen ? 15 : hasWeeklyReview ? 12 : 14}
                                        maxPx={isFullscreen ? 20 : hasWeeklyReview ? 22 : 24}
                                        className="min-w-0 w-full text-center"
                                        textClassName="font-bold tabular-nums"
                                      >
                                        {displayPnl > 0 ? '+' : ''}{formatCurrencyAdaptive(displayPnl, { prefix: '' })}
                                      </AutoFitText>
                                    </div>
                                    {cardCount > 0 && (
                                      <div className={`${isFullscreen ? 'text-[10px] mt-0.5' : 'text-[9px] sm:text-[10px] md:text-[11px] mt-0 sm:mt-0.5'} font-medium theme-text text-center whitespace-nowrap`}>
                                        {cardCount} trade{cardCount !== 1 ? 's' : ''}
                                      </div>
                                    )}
                                  </div>
                                  {hasWeeklyReview && (
                                    <div className="theme-border theme-hover-surface flex w-[22%] items-center justify-center border-l theme-accent">
                                      <ChartBarSquareIcon className={`${isFullscreen ? 'h-5 w-5' : 'h-4 w-4 md:h-[18px] md:w-[18px]'}`} />
                                    </div>
                                  )}
                                </>
                              ) : null}
                            </CardElement>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </div>
            {/* Phones: the Week PnL column doesn't fit beside the days, so list it below. */}
            {!isFullscreen && (
              <ul className="mt-3 space-y-1.5 sm:hidden" aria-label="Week PnL">
                {calendarGrid.map((weekArr, weekIdx) => {
                  if (!weekArr.some(Boolean)) return null;
                  const { pnl, count } = weeklyPnls[weekIdx];
                  const days = weekArr.filter(Boolean);
                  const value = Number((Number(pnl) || 0).toFixed(2));
                  const weekHasReview = Boolean(
                    weeklyReviewState?.review
                    && weeklyReviewAnchorDay
                    && weekArr.includes(weeklyReviewAnchorDay)
                  );
                  return (
                    <li key={weekIdx} className="theme-panel theme-border flex items-center justify-between gap-3 rounded-card border px-3 py-2 text-sm">
                      <span className="theme-text-muted min-w-0 truncate">{monthName.slice(0, 3)} {days[0]}–{days[days.length - 1]}{count > 0 ? ` · ${count} trade${count !== 1 ? 's' : ''}` : ''}</span>
                      <span className="flex shrink-0 items-center gap-3">
                        {weekHasReview && (
                          <button
                            type="button"
                            onClick={() => setWeeklyReviewOpen(true)}
                            className="theme-accent text-xs font-medium hover:underline"
                            aria-label={`Open weekly review for ${weeklyReviewState.review.weekLabel}`}
                          >
                            Review
                          </button>
                        )}
                        <span
                          className="font-mono font-bold"
                          style={{ color: value > 0 ? 'var(--theme-success)' : value < 0 ? 'var(--theme-loss)' : 'var(--theme-text-muted)' }}
                        >
                          {value === 0 ? '-' : `${value > 0 ? '+' : ''}${formatCurrencyAdaptive(value, { prefix: '' })}`}
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
      {weeklyReviewState?.review && (
        <WeeklyReviewStrip
          review={weeklyReviewState.review}
          dataChanged={weeklyReviewState.dataChanged}
          onRefresh={handleWeeklyReviewRefresh}
          onSaveReflections={handleWeeklyReviewReflections}
          saving={weeklyReviewSaving}
          statusLabel={weeklyReviewState.localSafetyGate ? 'Local preview - Notebook unchanged' : undefined}
          hideTrigger
          open={weeklyReviewOpen}
          onOpenChange={setWeeklyReviewOpen}
        />
      )}
      <DayScoreCardModal
        isOpen={!!selectedDayCard}
        onClose={() => setSelectedDayCard(null)}
        dateKey={selectedDayCard?.dateKey}
        dateLabel={selectedDayCard?.dateLabel}
        pnl={selectedDayCard?.pnl}
        rr={selectedDayCard?.rr}
        percentage={selectedDayCard?.percentage}
        session={selectedDayCard?.session}
        grade={selectedDayCard?.grade}
      />
      <DayScoreCardModal
        isOpen={isMonthCardOpen}
        onClose={() => setIsMonthCardOpen(false)}
        cardType="month"
        heading="Month Score Card"
        metricLabel="Net month PnL"
        dateKey={`${year}-${String(month).padStart(2, '0')}`}
        dateLabel={monthName}
        pnl={monthPnl}
        percentage={monthPerformance?.percentage}
        statCards={monthCardStats}
        notesLabel="Month notes"
        notesPlaceholder={`Add a clean note for ${monthName} ${year}...`}
        fileNamePrefix="month-card"
      />
    </div>
  );
};

export default MonthPage; 
